"use client";
import { useState, useEffect } from "react";
import "../css/EditRetailer.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";

const EditRetailer = () => {
  const location = useLocation();
  const {
    employeeId,
    assignedCities = [],
    routes: initialRoutes = [],
    areas: initialAreas = [],
  } = location.state || {};

  const [cities, setCities] = useState(assignedCities);
  const [routes, setRoutes] = useState(initialRoutes);
  const [areas, setAreas] = useState(initialAreas);
  const navigate = useNavigate();
  const retailerCode = new URLSearchParams(location.search).get("chemist_code");
  const [chemistId, setChemistId] = useState(null);

  const [formData, setFormData] = useState({
    chemist_name: "",
    contact_person: "",
    country: "India",
    state_id: "",
    city_id: "",
    area_id: "",
    route_id: "",
    phone: "",
    mobile: "",
    email: "",
    address: "",
    pin: "",
    address_map: "",
    gstin: "",
    cst_number: "",
    tin_vat_number: "",
    dl_number: "",
    pan_number: "",
    dob: "",
    anniversary: "",
    is_active: true,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [states, setStates] = useState([]);
  const previousState = location.state || {};
  const previousFilters = previousState.filters || {};
  const selectedEmployeeName = previousState.selectedEmployeeName || "";

  useEffect(() => {
    const fetchData = async () => {
      if (!retailerCode) {
        setError("Invalid or missing chemist code.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch states, retailer, and cities in parallel
        const [statesRes, retailerRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/chemist/chemist/${retailerCode}`, { headers: getAuthHeaders() }),
        ]);

        const retailerData = retailerRes.data || {};
        setStates(statesRes.data || []);
        
        console.log("raw retailerRes.data ->", retailerData);

        if (!retailerData || !retailerData.id) {
          setError("Chemist not found.");
          setLoading(false);
          return;
        }

        const cityId = retailerData.city_id;
        const areaId = retailerData.area_id ?? null;

        // Load cities using employee_id
        if (employeeId) {
          const citiesRes = await axios.get(
            `${BASE_URL}/api/assigned-cities/${employeeId}`,
            { headers: getAuthHeaders() }
          );
          setCities(citiesRes.data || []);
        } else {
          setCities([]);
        }

        // Load areas and routes using city_id
        if (cityId) {
          const [areasRes, routesRes] = await Promise.all([
            axios.get(`${BASE_URL}/api/areas/${cityId}`, {
              headers: getAuthHeaders(),
            }),
            employeeId
              ? axios.get(
                  `${BASE_URL}/api/routes/${employeeId}/?city_id=${cityId}`,
                  {
                    headers: getAuthHeaders(),
                  }
                )
              : Promise.resolve({ data: [] }),
          ]);

          setAreas(areasRes.data || []);
          setRoutes(routesRes.data || []);
        } else {
          setAreas([]);
          setRoutes([]);
        }

        const normalized = {
          chemist_name: retailerData.chemist_name ?? "",
          contact_person: retailerData.contact_person ?? "",
          country: retailerData.country ?? "India",
          state_id: retailerData.state_id ? String(retailerData.state_id) : "",
          city_id: retailerData.city_id ? String(retailerData.city_id) : "",
          area_id: retailerData.area_id ? String(retailerData.area_id) : "",
          route_id: retailerData.route_id ? String(retailerData.route_id) : "",
          phone: retailerData.phone ?? "",
          mobile: retailerData.mobile ?? "",
          email: retailerData.email ?? "",
          address: retailerData.address ?? "",
          pin: retailerData.pin ?? "",
          address_map: retailerData.address_map ?? "",
          gstin: retailerData.gstin ?? "",
          cst_number: retailerData.cst_number ?? "",
          tin_vat_number: retailerData.tin_vat_number ?? "",
          dl_number: retailerData.dl_number ?? "",
          pan_number: retailerData.pan_number ?? "",
          dob: retailerData.dob ? retailerData.dob.split("T")[0] : "",
          anniversary: retailerData.anniversary ? retailerData.anniversary.split("T")[0] : "",
          is_active: retailerData.is_active ?? true,
        };

        setChemistId(retailerData.id);
        console.log("normalized ->", normalized);
        setFormData((prev) => ({ ...prev, ...normalized }));
      } catch (err) {
        console.error("Error fetching chemist details:", err);
        setError(
          err?.response?.data?.detail
            ? `Error: ${err.response.data.detail}`
            : "Failed to load chemist details."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [retailerCode, employeeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state_id: stateId,
      city_id: "",
      area_id: "",
      route_id: "",
    }));
    setCities([]);
    setAreas([]);
    setRoutes([]);
    
    // Fetch cities for selected state if needed
    if (stateId && employeeId) {
      // Add API call here if you have state-based city filtering
      // For now, just reset the form fields
    }
  };

  const handleCityChange = async (cityId) => {
    setRoutes([]);
    setAreas([]);
    setFormData((prev) => ({
      ...prev,
      area_id: "",
      route_id: "",
    }));
    
    try {
      const [routesRes, areasRes] = await Promise.all([
        employeeId 
          ? axios.get(`${BASE_URL}/api/routes/${employeeId}/?city_id=${cityId}`, {
              headers: getAuthHeaders(),
            })
          : Promise.resolve({ data: [] }),
        axios.get(`${BASE_URL}/api/areas/${cityId}`, {
          headers: getAuthHeaders(),
        }),
      ]);
      setRoutes(routesRes.data || []);
      setAreas(areasRes.data || []);
    } catch (err) {
      console.warn("City change fetch error:", err);
      setRoutes([]);
      setAreas([]);
    }
  };

  const handleAreaChange = async (e) => {
    const areaId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      area_id: areaId,
      route_id: "",
    }));
    
    // Fetch routes when area changes
    if (employeeId && formData.city_id) {
      try {
        const routesRes = await axios.get(
          `${BASE_URL}/api/routes/${employeeId}/?city_id=${formData.city_id}`,
          { headers: getAuthHeaders() }
        );
        setRoutes(routesRes.data || []);
      } catch (err) {
        console.warn("Error fetching routes:", err);
        setRoutes([]);
      }
    }
  };

  const handleReturn = () => {
    navigate("/ViewChemist", {
      state: {
        fromEdit: true,
        shouldRefresh: true,
        filters: previousFilters,
        selectedEmployeeName: selectedEmployeeName,
      },
    });
  };

  const handleUpdate = async () => {
    try {
      if (!chemistId) {
        alert("No Chemist ID found for update.");
        return;
      }

      const payload = { ...formData };

      // Convert IDs to numbers or null
      payload.state_id = payload.state_id ? Number(payload.state_id) : null;
      payload.city_id = payload.city_id ? Number(payload.city_id) : null;
      payload.area_id = payload.area_id ? Number(payload.area_id) : null;
      payload.route_id = payload.route_id ? Number(payload.route_id) : null;

      const nullableFields = [
        "address", "pin", "address_map", "gstin", "cst_number",
        "tin_vat_number", "dl_number", "pan_number", "dob",
        "anniversary", "phone", "mobile", "email",
      ];

      nullableFields.forEach((field) => {
        if (payload[field] === "") payload[field] = null;
      });

      payload.is_active = !!payload.is_active;

      console.log("Final payload going to API =>", payload);

      await axios.put(
        `${BASE_URL}/api/chemist/chemist/${retailerCode}`,
        payload,
        { headers: getAuthHeaders() }
      );

      alert("Updated successfully!");
      handleReturn();
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      alert(`Update failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  /* ─── shared class strings ──────────────────────────────────────────── */
  const ROW_GRID =
    "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4";
  const FIELD = "flex items-center gap-4";
  const LBL = "font-medium whitespace-nowrap w-[130px] shrink-0";
  const LBL_REQUIRED = "text-orange-500 font-medium whitespace-nowrap w-[130px] shrink-0";
  const INPUT = "flex-1 min-w-0 border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const SELECT = "flex-1 min-w-0 border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500";
  /* ─────────────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xl font-semibold text-blue-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md p-6">
            <div className="text-red-600 font-bold mb-4">{error}</div>
            <button
              onClick={handleReturn}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-800 font-medium"
            >
              RETURN
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="w-full">
        <fieldset className="border-2 border-gray-400 p-6 bg-white">
          <legend className="text-lg font-semibold px-2">
            Chemist Basic Details -{" "}
            <span className="text-blue-600">{retailerCode}</span>
          </legend>

          <div className="text-right text-orange-500 text-sm mb-4">
            * Means Mandatory Field.
          </div>

          {/* ── BASIC DETAILS ──────────────────────────────────────────── */}
          <fieldset className="border border-gray-300 mb-6">
            <legend className="bg-green-100 border border-green-300 px-4 py-2 font-semibold text-green-800">
              Basic Details
            </legend>

            <div className="p-4 space-y-4">

              {/* Row 1 — Chemist Name | Contact Person */}
              <div className={ROW_GRID}>
                <div className={FIELD}>
                  <label className={LBL_REQUIRED}>Chemist Name</label>
                  <input
                    type="text"
                    name="chemist_name"
                    value={formData.chemist_name || ""}
                    onChange={handleInputChange}
                    className={INPUT}
                    required
                  />
                </div>
                <div className={FIELD}>
                  <label className={LBL}>Contact Person</label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person || ""}
                    onChange={handleInputChange}
                    className={INPUT}
                  />
                </div>
              </div>

              {/* Row 2 — Country | State */}
              <div className={ROW_GRID}>
                <div className={FIELD}>
                  <label className={LBL_REQUIRED}>Country</label>
                  <select
                    name="country"
                    value={formData.country || ""}
                    onChange={handleInputChange}
                    className={SELECT}
                  >
                    <option value="India">India</option>
                  </select>
                </div>
                <div className={FIELD}>
                  <label className={LBL}>State</label>
                  <select
                    name="state_id"
                    value={formData.state_id || ""}
                    onChange={handleStateChange}
                    className={SELECT}
                  >
                    <option value="">Select State</option>
                    {states?.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3 — City | Area */}
              <div className={ROW_GRID}>
                <div className={FIELD}>
                  <label className={LBL}>City</label>
                  <select
                    name="city_id"
                    value={formData.city_id || ""}
                    onChange={(e) => {
                      const cityId = e.target.value;
                      setFormData((prev) => ({ ...prev, city_id: cityId, area_id: "", route_id: "" }));
                      if (cityId) handleCityChange(cityId);
                    }}
                    className={SELECT}
                  >
                    <option value="">Select City</option>
                    {cities.map((c) => (
                      <option key={c.id || c.city_id} value={c.id || c.city_id}>
                        {c.city_name || c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={FIELD}>
                  <label className={LBL}>Area</label>
                  <select
                    name="area_id"
                    value={formData.area_id || ""}
                    onChange={handleAreaChange}
                    className={SELECT}
                  >
                    <option value="">Select Area</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.area_name || a.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4 — Route | Mobile Number */}
              <div className={ROW_GRID}>
                <div className={FIELD}>
                  <label className={LBL_REQUIRED}>Route</label>
                  <select
                    name="route_id"
                    value={formData.route_id || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, route_id: e.target.value }))
                    }
                    className={SELECT}
                  >
                    <option value="">Select Route</option>
                    {routes.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.route_name || r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={FIELD}>
                  <label className={LBL}>Mobile Number</label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile || ""}
                    onChange={handleInputChange}
                    className={INPUT}
                  />
                </div>
              </div>

              {/* Row 5 — Phone Number | Active Status */}
              <div className={ROW_GRID}>
                <div className={FIELD}>
                  <label className={LBL_REQUIRED}>Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone || ""}
                    onChange={handleInputChange}
                    className={INPUT}
                  />
                </div>
                <div className={FIELD}>
                  <label className={LBL}>Active Status</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!!formData.is_active}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, is_active: true }))
                        }
                        className="w-4 h-4"
                      />
                      <span>ACTIVE</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!formData.is_active}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, is_active: false }))
                        }
                        className="w-4 h-4"
                      />
                      <span>Inactive</span>
                    </label>
                  </div>
                </div>
              </div>

            </div>
          </fieldset>

          {/* ── CONTACT + CORPORATE ────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Contact Details */}
            <fieldset className="border border-gray-300">
              <legend className="bg-green-100 border border-green-300 px-4 py-2 font-semibold text-green-800">
                Contact Details
              </legend>
              <div className="p-4 space-y-3">
                {[
                  ["Date of Birth", "dob", "date"],
                  ["Marriage Anniversary", "anniversary", "date"],
                  ["E-MAIL", "email", "email"],
                ].map(([label, name, type]) => (
                  <div key={name} className={FIELD}>
                    <label className="font-medium w-[180px] shrink-0">{label}</label>
                    <input
                      type={type}
                      name={name}
                      value={formData[name] || ""}
                      onChange={handleInputChange}
                      className={INPUT}
                    />
                  </div>
                ))}
                <div className={FIELD}>
                  <label className="font-medium w-[180px] shrink-0">Address</label>
                  <textarea
                    name="address"
                    rows="4"
                    value={formData.address || ""}
                    onChange={handleInputChange}
                    className={`${INPUT} resize-none`}
                  />
                </div>
                <div className={FIELD}>
                  <label className="font-medium w-[180px] shrink-0">PIN</label>
                  <input
                    type="text"
                    name="pin"
                    value={formData.pin || ""}
                    onChange={handleInputChange}
                    className={INPUT}
                  />
                </div>
              </div>
            </fieldset>

            {/* Corporate Details */}
            <fieldset className="border border-gray-300">
              <legend className="bg-green-100 border border-green-300 px-4 py-2 font-semibold text-green-800">
                Corporate Details
              </legend>
              <div className="p-4 space-y-3">
                {[
                  ["GST Number", "gstin"],
                  ["CST Number", "cst_number"],
                  ["TIN/VAT Number", "tin_vat_number"],
                  ["DL Number", "dl_number"],
                  ["PAN Number", "pan_number"],
                ].map(([label, key]) => (
                  <div key={key} className={FIELD}>
                    <label className="font-medium w-[180px] shrink-0">{label}</label>
                    <input
                      type="text"
                      name={key}
                      value={formData[key] || ""}
                      onChange={handleInputChange}
                      className={INPUT}
                    />
                  </div>
                ))}
              </div>
            </fieldset>

          </div>

          {/* ── Buttons ─────────────────────────────────────────────────── */}
          <div className="mt-8 flex justify-center gap-6">
            <CustomButton
              onClick={handleUpdate}
              variant="save"
              className="text-sm font-medium"
            >
              UPDATE
            </CustomButton>
            <CustomButton
              onClick={handleReturn}
              variant="cancel"
              className="text-sm font-medium"
            >
              RETURN
            </CustomButton>
          </div>

        </fieldset>
      </div>
    </div>
  );
};

export default EditRetailer;